import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Star, ChevronDown, ChevronRight, Menu, X, Phone, Mail, Instagram,
  Home as HomeIcon,
} from 'lucide-react';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import { useLang } from '../i18n/LanguageContext';
import { navItems, brand, serviceCategories } from '../mock';

const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [activeCat, setActiveCat] = useState(serviceCategories[0].id);
  const [mobileSvcOpen, setMobileSvcOpen] = useState(false);
  const [mobileExpandedCat, setMobileExpandedCat] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const currentCat = serviceCategories.find((c) => c.id === activeCat) || serviceCategories[0];

  return (
    <header className="sticky top-0 z-50">
      {/* Top bar */}
      <div className={`transition-all duration-500 ${scrolled ? 'h-0 overflow-hidden opacity-0' : 'h-auto opacity-100'} bg-[#072A1F] border-b border-[#124D3A]`}>
        <div className="max-w-[1440px] mx-auto px-6 py-2.5 flex items-center justify-between text-xs text-[#ffffff]/85">
          <div className="flex items-center gap-6">
            <a href="/#reviews" className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
              <Star className="w-3.5 h-3.5 fill-[#FBBC05] text-[#FBBC05]" />
              <span className="hidden sm:inline">{t('topbar.reviews')}</span>
              <span className="text-[#ffffff]/40">|</span>
              <span className="font-medium">{brand.rating}</span>
              <span className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 fill-[#FBBC05] text-[#FBBC05]" />
                ))}
              </span>
            </a>
            <div className="hidden lg:block text-[#ffffff]/70">
              {t('topbar.patientsCountries', null, { patients: brand.happyPatients, countries: brand.countries })}
            </div>
          </div>
          <div className="hidden md:flex items-center gap-5">
            {brand.phones.map((p, i) => (
              <a key={p.tel} href={`tel:${p.tel}`} className="flex items-center gap-1.5 hover:text-[#D4AF37] transition-colors">
                <Phone className="w-3 h-3" />
                <span className="text-sm">{p.flag}</span>
                <span>{p.value}</span>
                {i === 0 && <span className="text-[#ffffff]/30 ml-4">|</span>}
              </a>
            ))}
            <span className="text-[#ffffff]/30">|</span>
            <a href={`mailto:${brand.email}`} className="flex items-center gap-1.5 hover:text-[#D4AF37] transition-colors">
              <Mail className="w-3 h-3" />
              {brand.email}
            </a>
            <a href={brand.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
              className="flex items-center gap-1.5 hover:text-[#D4AF37] transition-colors">
              <Instagram className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className={`backdrop-blur-md transition-all duration-300 relative ${scrolled ? 'bg-[#0B3D2E]/95 shadow-lg shadow-black/20' : 'bg-[#0B3D2E]/80'}`}>
        <div className="max-w-[1440px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" aria-label="Gold Care Esthetic home">
            <Logo size="md" />
          </Link>

          {/* Desktop nav pill */}
          <nav
            className="hidden lg:flex items-center relative"
            onMouseLeave={() => setServicesOpen(false)}
          >
            <ul className="flex items-center gap-0 bg-[#124D3A]/60 rounded-full px-2 py-1.5 border border-[#D4AF37]/10">
              {navItems.map((item) => (
                <li
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => item.hasMenu && setServicesOpen(true)}
                >
                  {item.hasMenu ? (
                    <>
                      <button
                        onClick={() => setServicesOpen((v) => !v)}
                        className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm text-[#ffffff] hover:bg-[#D4AF37]/15 transition-all ${
                          servicesOpen ? 'bg-[#124D3A]' : ''
                        }`}
                      >
                        {t(item.i18nKey, item.label)}
                        <ChevronDown
                          className={`w-3.5 h-3.5 opacity-80 transition-transform duration-300 ${
                            servicesOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      {/* Services Cascading Dropdown anchored under this button */}
                      {servicesOpen && (
                        <div
                          className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50"
                          onMouseEnter={() => setServicesOpen(true)}
                        >
                          <div className="flex bg-[#083325] rounded-3xl border border-[#D4AF37]/25 shadow-2xl shadow-black/60 overflow-hidden">
                            {/* Categories column */}
                            <div className="w-[300px] py-2 border-r border-[#D4AF37]/15 bg-[#124D3A]">
                              {serviceCategories.map((cat) => (
                                <button
                                  key={cat.id}
                                  onMouseEnter={() => setActiveCat(cat.id)}
                                  className={`w-full flex items-center justify-between gap-3 mx-2 my-0.5 px-5 py-3 rounded-2xl text-left transition-all ${
                                    activeCat === cat.id
                                      ? 'bg-[#083325] text-[#ffffff]'
                                      : 'text-[#ffffff]/85 hover:bg-[#175C45]/70'
                                  }`}
                                  style={{ width: 'calc(100% - 16px)' }}
                                >
                                  <span className="text-[16px] font-serif-display">{t(`cat.${cat.id}`, cat.label)}</span>
                                  <ChevronRight
                                    className={`w-4 h-4 transition-transform ${
                                      activeCat === cat.id
                                        ? 'text-[#D4AF37] translate-x-0.5'
                                        : 'text-[#ffffff]/50'
                                    }`}
                                  />
                                </button>
                              ))}
                            </div>

                            {/* Sub-treatments column */}
                            <div className="w-[300px] py-2 bg-[#083325]">
                              {currentCat.treatments.map((tr) => (
                                <Link
                                  key={tr.slug}
                                  to={`/treatments/${tr.slug}`}
                                  onClick={() => setServicesOpen(false)}
                                  className="block mx-2 my-0.5 px-5 py-3 rounded-2xl text-[16px] font-serif-display text-[#ffffff]/90 hover:bg-[#1B6A50] hover:text-[#ffffff] transition-all"
                                >
                                  {t(`tr.${tr.slug}`, tr.label)}
                                </Link>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm text-[#ffffff] hover:bg-[#D4AF37]/15 transition-all ${
                        item.icon === 'home' ? 'bg-[#175C45]' : ''
                      }`}
                    >
                      {item.icon === 'home' && <HomeIcon className="w-3.5 h-3.5" />}
                      {t(item.i18nKey, item.label)}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              to="/#contact-form"
              className="btn-gold px-7 py-2.5 rounded-full text-sm font-semibold inline-block"
            >
              {t('cta.freeConsultation')}
            </Link>
          </div>

          {/* Mobile menu toggle */}
          <div className="lg:hidden flex items-center gap-3">
            <LanguageSwitcher compact />
            <button className="text-[#ffffff]" onClick={() => setMobileOpen((v) => !v)} aria-label="Toggle menu">
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-[#124D3A] border-t border-[#D4AF37]/20 max-h-[80vh] overflow-y-auto">
            <ul className="flex flex-col p-4">
              {navItems.map((item) =>
                item.hasMenu ? (
                  <li key={item.label}>
                    <button
                      onClick={() => setMobileSvcOpen((v) => !v)}
                      className="w-full flex items-center justify-between py-3 px-4 text-[#ffffff] border-b border-[#D4AF37]/10"
                    >
                      <span>{t(item.i18nKey, item.label)}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${mobileSvcOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {mobileSvcOpen && (
                      <div className="bg-[#0B3D2E]/60 py-1">
                        {serviceCategories.map((cat) => (
                          <div key={cat.id}>
                            <button
                              onClick={() => setMobileExpandedCat((v) => (v === cat.id ? null : cat.id))}
                              className="w-full flex items-center justify-between px-6 py-2.5 text-sm text-[#ffffff] border-b border-[#D4AF37]/5"
                            >
                              <span>{t(`cat.${cat.id}`, cat.label)}</span>
                              <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpandedCat === cat.id ? 'rotate-180' : ''}`} />
                            </button>
                            {mobileExpandedCat === cat.id && (
                              <div className="bg-[#072A1F] py-1">
                                {cat.treatments.map((tr) => (
                                  <Link
                                    key={tr.slug}
                                    to={`/treatments/${tr.slug}`}
                                    onClick={() => setMobileOpen(false)}
                                    className="block pl-10 pr-4 py-2 text-sm text-[#ffffff]/80 hover:text-[#D4AF37]"
                                  >
                                    {t(`tr.${tr.slug}`, tr.label)}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                ) : (
                  <li key={item.label}>
                    <Link
                      to={item.href}
                      onClick={() => setMobileOpen(false)}
                      className="block py-3 px-4 text-[#ffffff] border-b border-[#D4AF37]/10"
                    >
                      {t(item.i18nKey, item.label)}
                    </Link>
                  </li>
                )
              )}
              <li className="mt-4 space-y-2">
                {brand.phones.map((p) => (
                  <a key={p.tel} href={`tel:${p.tel}`} className="flex items-center gap-2 py-2 px-4 text-sm text-[#ffffff]/85">
                    <Phone className="w-4 h-4 text-[#D4AF37]" /> {p.flag} {p.value}
                  </a>
                ))}
                <Link
                  to="/#contact-form"
                  className="btn-gold block text-center py-3 rounded-full"
                  onClick={() => setMobileOpen(false)}
                >
                  {t('cta.freeConsultation')}
                </Link>
              </li>
            </ul>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
